import { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import { Capacitor } from '@capacitor/core'

export default function Billing() {
  if (Capacitor.isNativePlatform()) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>To manage your subscription, visit <strong>completept.app</strong> in your browser.</p>
      </div>
    )
  }

  const { profile } = useAuth()
  const navigate = useNavigate()
  const [subscription, setSubscription] = useState(null)
  const [clinicianCount, setClinicianCount] = useState(1)
  const [pricePerSeat, setPricePerSeat] = useState(19.99)
  const [seatCount, setSeatCount] = useState(1)
  const [updatingSeats, setUpdatingSeats] = useState(false)
  const [loading, setLoading] = useState(true)
  const [checkingOut, setCheckingOut] = useState(false)
  const [openingPortal, setOpeningPortal] = useState(false)
  const [error, setError] = useState('')
  const [seatSuccess, setSeatSuccess] = useState('')

  useEffect(() => { if (profile) fetchData() }, [profile])

  async function fetchData() {
    const [{ data: sub }, { data: clinicians }] = await Promise.all([
      supabase.from('subscriptions').select('*').eq('clinic_id', profile.clinic_id).single(),
      supabase.from('users').select('id').eq('clinic_id', profile.clinic_id).eq('role', 'clinician')
    ])
    setSubscription(sub)
    setClinicianCount(clinicians?.length || 1)
    setSeatCount(sub?.seat_count || clinicians?.length || 1)
    try {
      const res = await fetch('/.netlify/functions/get-price')
      if (res.ok) {
        const data = await res.json()
        setPricePerSeat(data.unit_amount / 100)
      }
    } catch {}
    setLoading(false)
  }

  function trialDaysLeft() {
    if (!subscription?.trial_ends_at) return 0
    const diff = new Date(subscription.trial_ends_at) - new Date()
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
  }

  function isTrialActive() {
    return subscription?.status === 'trialing' && trialDaysLeft() > 0
  }

  function isActive() {
    return subscription?.status === 'active'
  }

  async function handleCheckout() {
    setCheckingOut(true)
    setError('')
    try {
      const res = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clinic_id: profile.clinic_id,
          clinic_name: profile.clinics?.name,
          seat_count: seatCount,
          customer_id: subscription?.stripe_customer_id || null
        })
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error); setCheckingOut(false); return }
      window.location.href = data.url
    } catch {
      setError('Something went wrong. Please try again.')
      setCheckingOut(false)
    }
  }

  async function handlePortal() {
    setOpeningPortal(true)
    try {
      const res = await fetch('/.netlify/functions/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: subscription?.stripe_customer_id })
      })
      const data = await res.json()
      window.location.href = data.url
    } catch {
      setError('Something went wrong.')
      setOpeningPortal(false)
    }
  }

  async function handleUpdateSeats() {
    if (seatCount < clinicianCount) {
      setError('You cannot have fewer seats than active clinicians (' + clinicianCount + ').')
      return
    }
    setUpdatingSeats(true)
    setError('')
    setSeatSuccess('')
    try {
      const res = await fetch('/.netlify/functions/update-seats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscription_id: subscription.stripe_subscription_id,
          seat_count: seatCount,
          clinic_id: profile.clinic_id
        })
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error); setUpdatingSeats(false); return }
      setSeatSuccess('Seats updated successfully.')
      setSubscription(prev => ({ ...prev, seat_count: seatCount }))
    } catch {
      setError('Something went wrong.')
    }
    setUpdatingSeats(false)
  }

  const daysLeft = trialDaysLeft()
  const monthlyTotal = (seatCount * pricePerSeat).toFixed(2)

  if (loading || !profile) return <><Navbar /><div className="page"><p>Loading...</p></div></>

  return (
    <>
      <Navbar />
      <div className="page">
        <span onClick={() => navigate('/clinician')} className="back-link" style={{ cursor: 'pointer' }}>Back to Dashboard</span>
        <div className="page-header">
          <h1>Billing</h1>
          <p>{profile?.clinics?.name}</p>
        </div>

        {error && <div className="alert-error" style={{ marginBottom: '1.5rem' }}>{error}</div>}
        {seatSuccess && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', padding: '12px 16px', borderRadius: 10, marginBottom: '1.5rem', fontSize: 13, fontWeight: 500 }}>{seatSuccess}</div>}

        <div style={{
          background: isActive() ? 'linear-gradient(135deg, #22c55e, #16a34a)' : isTrialActive() ? 'linear-gradient(135deg, #6366f1, #4f46e5)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
          borderRadius: 16, padding: '1.5rem', marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>Status</p>
              <p style={{ color: 'white', fontSize: 26, fontWeight: 800, margin: '0 0 6px', letterSpacing: -0.5 }}>
                {isActive() ? 'Active' : isTrialActive() ? 'Free trial' : 'Trial expired'}
              </p>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, margin: 0 }}>
                {isActive() && subscription?.current_period_ends_at
                  ? 'Renews ' + new Date(subscription.current_period_ends_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                  : isTrialActive()
                  ? 'Trial ends ' + new Date(subscription.trial_ends_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                  : 'Add a payment method to continue using Complete PT'}
              </p>
            </div>
            {isTrialActive() && (
              <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                <p style={{ color: 'white', fontSize: 32, fontWeight: 800, margin: 0, letterSpacing: -1 }}>{daysLeft}</p>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, margin: 0 }}>days left</p>
              </div>
            )}
          </div>
        </div>

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <p style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 15, margin: '0 0 1rem' }}>Plan summary</p>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
            <p style={{ margin: 0, fontSize: 14 }}>Active clinicians</p>
            <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>{clinicianCount}</p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
            <p style={{ margin: 0, fontSize: 14 }}>Price per seat</p>
            <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-primary)', fontSize: 14 }}>${pricePerSeat.toFixed(2)}/month</p>
          </div>
          {profile?.is_admin && (
            <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <p style={{ margin: 0, fontSize: 14 }}>Licensed seats</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button type="button" className="secondary" onClick={() => setSeatCount(prev => Math.max(clinicianCount, prev - 1))} style={{ width: 32, height: 32, padding: 0, fontSize: 18, borderRadius: 8 }}>-</button>
                  <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)', minWidth: 24, textAlign: 'center' }}>{seatCount}</span>
                  <button type="button" className="secondary" onClick={() => setSeatCount(prev => prev + 1)} style={{ width: 32, height: 32, padding: 0, fontSize: 18, borderRadius: 8 }}>+</button>
                </div>
              </div>
              {seatCount < clinicianCount && (
                <p style={{ fontSize: 12, color: 'var(--danger)', margin: 0 }}>Minimum {clinicianCount} seats required</p>
              )}
              {isActive() && seatCount !== subscription?.seat_count && (
                <button onClick={handleUpdateSeats} disabled={updatingSeats || seatCount < clinicianCount} style={{ width: '100%', padding: 10, fontSize: 13, marginTop: 8 }}>
                  {updatingSeats ? 'Updating...' : 'Update seats — $' + monthlyTotal + '/month'}
                </button>
              )}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0 0' }}>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Total</p>
            <p style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)', fontSize: 20, letterSpacing: -0.5 }}>
              ${monthlyTotal}<span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-tertiary)' }}>/month</span>
            </p>
          </div>
        </div>

        {profile?.is_admin ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {!isActive() && (
              <button onClick={handleCheckout} disabled={checkingOut} style={{ width: '100%', padding: 16, fontSize: 16 }}>
                {checkingOut ? 'Redirecting to checkout...' : 'Subscribe — $' + monthlyTotal + '/month'}
              </button>
            )}
            {isActive() && subscription?.stripe_customer_id && (
              <button className="secondary" onClick={handlePortal} disabled={openingPortal} style={{ width: '100%', padding: 14 }}>
                {openingPortal ? 'Opening...' : 'Manage subscription and invoices'}
              </button>
            )}
            <p style={{ fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center', margin: 0 }}>
              Seats are billed monthly. Cancel anytime.
            </p>
          </div>
        ) : (
          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '1.25rem', textAlign: 'center' }}>
            <p style={{ fontSize: 14, margin: 0, color: 'var(--text-secondary)' }}>Contact your clinic admin to manage billing.</p>
          </div>
        )}
      </div>
    </>
  )
}
